import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../toastify/Toast";
import { handleApiError } from "../../utils/handleApiError";
import { resetNewPackageTitleData, resetPackageState, selectCurrentPackageItems, selectedNewPackageTitleData, selectPackageDetailsAction, selectPackagedId, setPackageDetails } from "../../features/package/packageSlice";
import { clearTypeModal, clearTypeOfShowModalType, clearSelectedId } from "../../features/modal/modalSlice";
import { clearSelectedProductToAddQty } from "../../features/product/productSlice";
import { useMutationData } from "../api/useMutationData";
import { useQueryData } from "../api/useQueryData";
import { ProductPackage } from "../../types/package";
import { UpdatePackageItemRequestBody, UpdateSpecialPackageDetailsRequestBody, UpdateSpecialPackageItemRequestBody } from "../../features/package/packageRequestBody";

interface useSavePackageProps {
    typOfAction: "newPackage" | "updatePackage";
}

export function useSavePackage({ typOfAction }: useSavePackageProps) {

    const dispatch = useDispatch();
    const newPackageTitleData = useSelector(selectedNewPackageTitleData);
    const currentPackageItems = useSelector(selectCurrentPackageItems);
    const selectedPackageId = useSelector(selectPackagedId)
    const packageDetailAction = useSelector(selectPackageDetailsAction);
    const isPackageCupBase = packageDetailAction.packageType === 'special'
    const {
        createPackage,
        isLoadingCreatePackage,
        isErrorCreatePackage,

        createNewPackageItems,
        isLoadingCreateNewPackageItems,
        isErrorCreateNewPackageItems,

        updatePackageItems,
        isLoadingUpdatePackageItems,

        updatePackageDetails,
        isLoadingUpdatePackageDetails,

        updateSpecialPackageDetails,
        isLoadingUpdateSpecialPackageDetails,

        updateSpecialPackageItems,
        isLoadingUpdateSpecialPackageItems,
    } = useMutationData()

    const {
        packageData,
        isErrorPackageData: isErrorPackageItems,
        isLoadingPackageData: isLoadingPackageItems,
        refetchPackageData,
    } = useQueryData()
    
    const handleSavePackage = async () => {
        if (!newPackageTitleData.packageName || newPackageTitleData.quantity == null || newPackageTitleData.totalValue == null ) {
            showToast("Please set package name, cups, and amount.", "info");
            return;
        }

        try {
            if (typOfAction === "newPackage") {
                const productIDs = currentPackageItems.map((item: ProductPackage) => item.productId);
                if(productIDs.length === 0) return showToast('Please add at least one product to the package.', "info")
                const result = await createPackage(newPackageTitleData).unwrap();
                const packageID = result.packageID;

                if (!packageID) {
                    showToast("No package ID returned", "error");
                    return;
                }
                await createNewPackageItems({ packageID, productIDs, }).unwrap();
                showToast("Package created successfully", "success");
            } else if (typOfAction === "updatePackage") {
                if (!selectedPackageId) return;
                if (isPackageCupBase) {
                    if ( newPackageTitleData.discountPercentage == null || newPackageTitleData.canExceed == null ) {
                        showToast( "Please set the discount percentage and exceed option.", "info" );
                        return;
                    }

                    const body: UpdateSpecialPackageDetailsRequestBody = {
                        packageName: newPackageTitleData.packageName,
                        packageAmount: newPackageTitleData.totalValue,
                        discountPercentage: newPackageTitleData.discountPercentage,
                        description: newPackageTitleData.description ?? "",
                        canExceed: newPackageTitleData.canExceed,
                    };
                    const selectedProductIds = currentPackageItems.map((item: ProductPackage) => item.productId)
                    const productIDs: UpdateSpecialPackageItemRequestBody = {
                        productIDs: selectedProductIds
                    };
                    if(selectedProductIds.length === 0) return showToast('Please add at least one product to the package.', "info")
                    
                    await Promise.all([
                        updateSpecialPackageDetails({ packageId: selectedPackageId, body, }).unwrap(),
                        updateSpecialPackageItems({ packageId: selectedPackageId, body: productIDs, }).unwrap(),
                    ]);
                } else {
                    const payload: UpdatePackageItemRequestBody = {
                        packageId: selectedPackageId,
                        products: currentPackageItems.map((item: ProductPackage) => ({ productID: item.productId, })),
                    };
                    const selectedProductIds = currentPackageItems.map((item: ProductPackage) => item.productId)
                    if(selectedProductIds.length === 0) return showToast('Please add at least one product to the package.', "info")
                    await Promise.all([
                        updatePackageDetails({
                            packageId: selectedPackageId,
                            packageDetails: {
                                packageName: newPackageTitleData.packageName,
                                quantity: newPackageTitleData.quantity,
                                totalValue: newPackageTitleData.totalValue,
                            },
                        }).unwrap(),
                        updatePackageItems(payload).unwrap(),
                    ]);
                }
                showToast("Package updated successfully", "success");
            }

            dispatch(clearTypeModal());
            dispatch(clearSelectedProductToAddQty());
            dispatch(clearTypeOfShowModalType());
            dispatch(clearSelectedId());
            dispatch(resetNewPackageTitleData());
            dispatch(resetPackageState());
            dispatch(setPackageDetails({packageType: null, typeOfAction: null}));
        } catch (error) {
            console.error("ERROR:", error);
            handleApiError(error, "Failed to save package.");
        }
    };

    return { 
        handleSavePackage,
        isErrorPackageItems,
        isLoadingPackageItems,
        refetchPackageData,
        isLoadingCreatePackage,
        isErrorCreatePackage,
        isLoadingCreateNewPackageItems,
        isErrorCreateNewPackageItems,
        isLoadingUpdatePackageItems,
        isLoadingUpdatePackageDetails,
        isLoadingUpdateSpecialPackageDetails,
        isLoadingUpdateSpecialPackageItems
    };
}